import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOMeta from '@/components/feature/SEOMeta';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    const formData = new FormData(form);
    const url = 'https://readdy.ai/api/form/d9sm3jp4ne17ei0sdjqg';

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
        setTimeout(() => { setFormStatus('idle'); setFormError(''); }, 4000);
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
      setTimeout(() => { setFormStatus('idle'); setFormError(''); }, 4000);
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '蘑菇视频是否完全免费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，蘑菇视频的所有内容均支持免费在线观看，无需付费订阅。我们通过广告收入维持平台运营，为用户提供免费的优质视频内容。',
        },
      },
      {
        '@type': 'Question',
        name: '蘑菇视频支持哪些设备观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '蘑菇视频支持手机、平板、电脑等多种设备观看。您可以通过浏览器直接访问我们的网站，无需下载任何软件。播放进度支持云端同步，随时随地接着看。',
        },
      },
      {
        '@type': 'Question',
        name: '视频画质最高支持多少？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '蘑菇视频支持最高4K超高清画质播放，同时提供1080P、720P、480P等多种画质选项。系统会根据您的网络状况自动调整最佳画质。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系蘑菇视频客服？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过以下方式联系我们：客服邮箱 contact@mogu-video.com，客服电话 400-888-9999（工作日 9:00-18:00），或者在网站留言表单提交您的问题。',
        },
      },
    ],
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '蘑菇视频',
    url: 'https://www.mogu-video.com',
    description: '蘑菇视频是您最佳的在线视频平台，汇聚海量高清影视、短视频、综艺、纪录片等优质内容。',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '望京科技园A座',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-9999',
      contactType: 'customer service',
      email: 'contact@mogu-video.com',
    },
  };

  return (
    <>
      <SEOMeta
        title="关于我们 - 蘑菇视频"
        description="蘑菇视频是国内领先的在线视频平台，致力于为用户提供海量高清影视、短视频、综艺、纪录片等优质内容。了解蘑菇视频的品牌故事、平台优势与联系方式。"
        keywords="蘑菇视频关于我们,蘑菇视频品牌介绍,蘑菇视频联系方式,在线视频平台"
        canonicalUrl="/about"
        schema={[faqSchema, orgSchema]}
      />

      <Navbar />

      <main className="pt-20 pb-16">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Brand Intro */}
          <section className="mt-8">
            <div className="text-center max-w-[720px] mx-auto">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-primary-100 mx-auto mb-5">
                <i className="ri-play-circle-fill text-3xl text-primary-600"></i>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900">
                关于蘑菇视频
              </h1>
              <p className="mt-4 text-[15px] text-foreground-600 leading-relaxed">
                蘑菇视频创立于2020年，是国内领先的在线视频内容平台。我们致力于汇聚全球优质视频资源，
                为用户提供高清流畅的在线观看体验。经过多年发展，蘑菇视频已经成为数千万用户信赖的视频平台，
                内容涵盖电影、电视剧、综艺、动漫、纪录片、短视频、体育和音乐等八大领域。
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: 'ri-eye-line', title: '我们的愿景', desc: '成为国内最受用户喜爱的在线视频平台，让每个人都能轻松享受高质量的视听内容。' },
                { icon: 'ri-heart-line', title: '我们的使命', desc: '用技术连接内容与用户，打造开放包容的视频生态，让优质内容触手可及。' },
                { icon: 'ri-star-line', title: '核心价值', desc: '用户至上、内容为王、技术创新、诚信共赢，用心做好每一个细节。' },
              ].map((item) => (
                <div key={item.title} className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mx-auto">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-foreground-900">{item.title}</h3>
                  <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Reputation / Achievements */}
          <section className="mt-16">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center">
              平台荣誉与口碑
            </h2>
            <p className="mt-3 text-sm text-foreground-500 text-center max-w-[480px] mx-auto">
              用户的认可和行业的肯定，是我们不断前进的动力
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { value: '4.8', unit: '分', label: '用户综合评分', desc: '来自超过50万真实用户评价' },
                { value: '98', unit: '%', label: '用户满意度', desc: '连续三年保持高满意度' },
                { value: '15', unit: '项', label: '行业奖项', desc: '获得多项行业权威奖项认可' },
                { value: '5000', unit: '万+', label: '累计用户', desc: '覆盖全国各省市自治区' },
              ].map((stat) => (
                <div key={stat.label} className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
                  <p className="text-3xl font-bold text-primary-600 font-heading">
                    {stat.value}<span className="text-lg font-normal">{stat.unit}</span>
                  </p>
                  <p className="mt-1 text-sm font-medium text-foreground-800">{stat.label}</p>
                  <p className="mt-1 text-xs text-foreground-400">{stat.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact & Form */}
          <section className="mt-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* Contact Info */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                  联系我们
                </h2>
                <p className="mt-3 text-sm text-foreground-500 leading-relaxed">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系。
                  我们的客服团队将在工作时间内尽快回复您。
                </p>
                <div className="mt-6 space-y-4">
                  {[
                    { icon: 'ri-mail-line', label: '客服邮箱', value: 'contact@mogu-video.com' },
                    { icon: 'ri-phone-line', label: '客服电话', value: '400-888-9999' },
                    { icon: 'ri-time-line', label: '工作时间', value: '周一至周五 9:00 - 18:00' },
                    { icon: 'ri-map-pin-line', label: '公司地址', value: '北京市朝阳区望京科技园A座' },
                  ].map((info) => (
                    <div key={info.label} className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                        <i className={`${info.icon} text-lg`}></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400">{info.label}</p>
                        <p className="text-sm font-medium text-foreground-800">{info.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <h3 className="text-lg font-semibold text-foreground-900 mb-5">在线留言</h3>
                  <form id="about-contact-form" data-readdy-form="" onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="about-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="about-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-background-300 bg-white text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="about-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-background-300 bg-white text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        留言内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="about-message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-background-300 bg-white text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                        placeholder="请输入您的留言（500字以内）"
                      ></textarea>
                    </div>
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="anti-spam-field" />
                    <button
                      type="submit"
                      className="w-full px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap"
                    >
                      提交留言
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-accent-600 text-center">提交成功！感谢您的留言，我们会尽快回复。</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="mt-16">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center">
              常见问题
            </h2>
            <p className="mt-3 text-sm text-foreground-500 text-center">
              您可能关心的一些问题
            </p>
            <div className="mt-8 max-w-[760px] mx-auto space-y-3">
              {[
                { q: '蘑菇视频是否完全免费？', a: '是的，蘑菇视频的所有内容均支持免费在线观看，无需付费订阅。我们通过广告收入维持平台运营，为用户提供免费的优质视频内容。' },
                { q: '蘑菇视频支持哪些设备观看？', a: '蘑菇视频支持手机、平板、电脑等多种设备观看。您可以通过浏览器直接访问我们的网站，无需下载任何软件。播放进度支持云端同步，随时随地接着看。' },
                { q: '视频画质最高支持多少？', a: '蘑菇视频支持最高4K超高清画质播放，同时提供1080P、720P、480P等多种画质选项。系统会根据您的网络状况自动调整最佳画质，确保流畅播放。' },
                { q: '如何联系蘑菇视频客服？', a: '您可以通过客服邮箱 contact@mogu-video.com、客服电话 400-888-9999（工作日 9:00-18:00），或在网站上提交留言表单与我们取得联系。我们的客服团队会尽快回复您。' },
                { q: '视频加载慢怎么办？', a: '我们建议您检查网络连接状况，或尝试切换至较低画质。蘑菇视频在全球部署了CDN加速节点，一般情况下可以保证流畅播放。如问题持续，请联系客服。' },
              ].map((faq, i) => (
                <details key={i} className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-200/50 transition-colors">
                    <span className="text-sm font-medium text-foreground-800 pr-4">{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}